import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Text } from '@mantine/core';
import { type FC, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHostContext } from '@/context/HostContext.tsx';
import type { CreatorRemoteProps } from '@/types/creator.mf.ts';
import StepperOnboardingFormContent from './components/StepperOnboardingFormContent';
import { StepperOnboardingProvider } from './context/StepperOnboardingContext';
import {
	createStepperDefaultFormValues,
	createStepperOnboardingSchema
} from './form';
import useStepperQueryParams from './hooks/useStepperQueryParams';
import { CLASS_NAMES } from './StepperOnboardingPage.classNames';
import type { StepperOnboardingFormValues } from './types';

interface StepperOnboardingPageProps {
	actor?: CreatorRemoteProps['actor'];
}

const StepperOnboardingPage: FC<StepperOnboardingPageProps> = ({ actor }) => {
	const { localization } = useHostContext();
	const stepperQueryParams = useStepperQueryParams();

	const formSchema = useMemo(() => {
		return createStepperOnboardingSchema({
			translate: localization.translate
		});
	}, [localization]);

	const formDefaultValues = useMemo(() => {
		return createStepperDefaultFormValues(localization.translate, {
			industry: stepperQueryParams.industry,
			managerFullName: stepperQueryParams.fullName,
			websiteUrl: stepperQueryParams.source ?? stepperQueryParams.googleMapUrl
		});
	}, [
		stepperQueryParams.fullName,
		stepperQueryParams.googleMapUrl,
		stepperQueryParams.industry,
		stepperQueryParams.source,
		localization
	]);

	const form = useForm<StepperOnboardingFormValues>({
		defaultValues: formDefaultValues,
		mode: 'onChange',
		reValidateMode: 'onChange',
		resolver: zodResolver(formSchema)
	});

	return (
		<Box
			className={CLASS_NAMES.contentShell}
			data-testid="stepper-onboarding-page"
		>
			<Text
				className={CLASS_NAMES.title}
				component="h1"
			>
				{localization.translate('creator', 'stepperOnboarding.title')}
			</Text>

			<FormProvider {...form}>
				<StepperOnboardingProvider actor={actor}>
					<StepperOnboardingFormContent />
				</StepperOnboardingProvider>
			</FormProvider>
		</Box>
	);
};

export default StepperOnboardingPage;
